import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { Metric } from "@/features/reports/components/metric";
import { useReportsContext } from "../../contexts/reports-context";
import { Bars3BottomLeftIcon, PhotoIcon } from "@heroicons/react/20/solid";
import { KeyDriversWrapper } from "./key-drivers-wrapper";
import { KeyDriverMetricTab } from "./key-driver-metric-tab";
import { EmotionRegionTable } from "./emotion-region-table";
import { CopilotTile } from "./copilot-tile";
import { EmotionScoreInfoModal } from "../../modals/emotion-score-info-modal";
import { ReactionTile } from "./reaction-tile";
import { InfoModalTrigger } from "@/components/ui/modal";
import { AOIEmotionInfoModal } from "../../modals/aoi-emotion-info-modal";
import { useSegment } from "@/hooks/use-segment";
import { EmotionMetric } from "../../contexts/reports-context";

/* Prop Types */
interface EmotionDetailsProps {
  currentAsset: AssetInsight;
  studyId: string;
  projectId: string;
}

/*
 * EmotionDetails component
 *
 * This component displays detailed information about the emotion metrics of a given asset for the in-depth report.
 */
export const EmotionDetails = ({ currentAsset, studyId, projectId }: EmotionDetailsProps) => {
  /* Context */
  const { state, actions } = useReportsContext();

  /* Consts */
  const regions = currentAsset?.emotion?.regions ?? [];

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleMetricChange = (newMetric: EmotionMetric) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Metric Changed", {
      reportType: "emotion",
      from: state.emotionMetric,
      to: newMetric,
      assetId: currentAsset.assetId,
      studyId,
      projectId,
    });

    // set the new metric
    actions.setEmotionMetric(newMetric);
  };

  /* Render */
  return (
    <>
      <div className="px-4 py-2 my-4">
        <Metric
          value={Math.round(currentAsset?.metrics?.emotion?.score ?? 0)}
          title="Emotion"
          size="xlarge"
          infoModal={<EmotionScoreInfoModal />}
          studyId={studyId}
          projectId={projectId}
          assetId={currentAsset.assetId}
          reportType="emotion"
        />
        <p className="text-sm text-base-content/70 mt-2">Is the asset likely to evoke a strong emotional response?</p>
      </div>

      <CopilotTile metric="emotion" />

      {/* Reaction */}
      {currentAsset?.metrics?.emotion?.reaction && <ReactionTile reaction={currentAsset.metrics.emotion.reaction} />}

      {/* Key Drivers */}
      <KeyDriversWrapper description="The primary elements influencing overall Emotion Intensity score. Other factors such as Attention, Visibility also contribute.">
        <div className="flex flex-row gap-2">
          <KeyDriverMetricTab
            onClick={() => handleMetricChange("visuals")}
            metric="visuals"
            metricIcon={<PhotoIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="emotion"
          />
          <KeyDriverMetricTab
            onClick={() => handleMetricChange("text")}
            metric="text"
            metricIcon={<Bars3BottomLeftIcon className="size-4 flex-shrink-0" />}
            currentAsset={currentAsset}
            reportType="emotion"
          />
        </div>
      </KeyDriversWrapper>

      {/* AOI Elements */}
      {regions.length > 0 && (
        <div className="p-4 rounded-lg bg-base-200 flex flex-col gap-3 mt-4">
          <div className="flex items-center gap-2">
            <p className="font-semibold">AOI elements</p>
            <InfoModalTrigger modalContent={<AOIEmotionInfoModal />} iconClassName="size-4" />
          </div>
          <p className="text-xs text-base-content/70">Elements that contribute towards {state.emotionMetric} score:</p>
          <EmotionRegionTable regions={regions} metric={state.emotionMetric} />
        </div>
      )}
    </>
  );
};
